import moment from 'moment';
import { storageFileIdFromUrl } from '../../../../../../../../functions/url.functions';
import { eChannelType } from '../../../../../../../../providers/SocketsProvider/Sockets.enums';
import { messageErrorTypeToMetaCode } from '../../../../../../../../utils/ErrorDirectory';
import {
  resolveHeldForQualityNotice,
  resolveLeadsalesRefusalNotice,
  resolveMetaErrorNotice,
  resolveWhatsappWebNewChatLimitNotice,
  resolveWhatsappWebRecipientNotFoundNotice,
} from '../../../../../../../../utils/metaErrorNotice';
import {
  tMetaErrorNotice,
  tMetaErrorNoticeCopyVariant,
} from '../../../../../../../../utils/metaErrorNotice.types';
import {
  EventType,
  MediaType,
  MessageErrorType,
  MessageState,
} from '../../../../../../Chat.enum';
import {
  tAttachment,
  tChatEvent,
  tChatEventError,
  tMessage,
} from '../../../../../../Chat.types';

/**
 * `chat/chat` key of the sender line when nobody in Leadsales sent the
 * message: it came from the channel's own app ("WhatsApp Web o celular").
 */
export const integrationBubbleTitleKey: Record<eChannelType, string> = {
  [eChannelType.INSTAGRAM]: 'messageBubble.channel.instagram',
  [eChannelType.WHATSAPP_WEB]: 'messageBubble.channel.whatsapp',
  [eChannelType.WHATSAPP_API]: 'messageBubble.channel.whatsapp',
  [eChannelType.MESSENGER]: 'messageBubble.channel.messenger',
  [eChannelType.TIKTOK]: 'messageBubble.channel.tiktok',
};

/**
 * Whether the message shows an image or a video. Both render at a fixed width
 * (w-72 in ImageContent and VideoContent), so the bubble takes that width and a
 * caption wraps under the media, as in WhatsApp. Reads the raw attachments:
 * decrypting one only swaps its URL, so the answer is known before it loads.
 */
export function hasVisualMedia(attachments?: tAttachment[] | null) {
  return Boolean(
    attachments?.some(
      (attachment) =>
        attachment?.mediaType === MediaType.IMAGE ||
        attachment?.mediaType === MediaType.VIDEO,
    ),
  );
}

// ErrorDirectory writes the 200-299 permission range as a sentinel; the
// notice catalog keys the same range as '200-299'.
const PERMISSION_RANGE_SENTINEL = 'PERMISSION_RANGE';

/**
 * What the notice resolvers read: the message, and the event's error rows,
 * which carry Meta's own code and details when WBS sent them (INT-1500 C3).
 * BUG-912: also the files the message carried, when the caller has them, so a
 * notice can tell a file of our own storage from any other link.
 */
export type tMetaErrorNoticeSource = Pick<tMessage, 'fromMe' | 'state' | 'error'> & {
  errors?: tChatEventError[];
  attachments?: Array<Pick<tAttachment, 'url'>> | null;
};

/**
 * The notice source of a chat event: its message plus its error rows and, when
 * the event has content, the files it carried.
 */
export function noticeSourceOf(
  event: Pick<tChatEvent, 'message' | 'errors'> &
    Partial<Pick<tChatEvent, 'content'>>,
): tMetaErrorNoticeSource | null {
  return event.message
    ? {
        ...event.message,
        errors: event.errors,
        attachments: event.content?.attachments,
      }
    : null;
}

/**
 * INT-1503: the notice for an outgoing message Meta refused to deliver, or
 * is still reviewing (INT-1500 U5, the pending variant), or undefined when
 * the bubble keeps its previous error display.
 *
 * Only an error that names a Meta code gets the Meta notice. Codes we mint
 * ourselves (`wba-*`: our upload hop, our rate limiter, our own failures) must
 * not reach the 131000 fallback, whose copy blames Meta. Neither must an error
 * with no Meta code at all. A Meta code missing from the catalog does fall back,
 * and still shows the code that arrived. A send Leadsales refused before Meta
 * (INT-1500 U7: back-off, opt-out, restricted-number pause, credits, empty
 * variables, a deleted scheduled template) gets its own "ours" notice.
 *
 * Entries written for inbound messages (131052, 131060, 131051) are only
 * shown on the lead's own messages: see resolveIncomingMetaErrorNotice.
 *
 * WhatsApp API chats only: the notice says WhatsApp did not deliver the
 * message, so any other channel keeps its own error display even if its error
 * happens to map to a Meta code. The exceptions are two WhatsApp Web refusals
 * that get their own notice: the new-chat limit (BUG-827, 2011) and a number
 * WhatsApp has no account for (BUG-1030, 1002).
 */
export function resolveOutgoingMetaErrorNotice(
  message: tMetaErrorNoticeSource | null | undefined,
  channel: eChannelType | string | undefined,
): tMetaErrorNotice | undefined {
  if (channel === eChannelType.WHATSAPP_WEB) {
    if (!message?.fromMe || message.state !== MessageState.ERROR) return undefined;
    if (message.error === MessageErrorType.WHATSAPP_WEB_NEW_CHAT_LIMIT_REACHED) {
      return resolveWhatsappWebNewChatLimitNotice();
    }
    // BUG-1030: chat code 1002 reaches the bubble under two names: the socket
    // event maps it to ERROR_RECIPIENT_NOT_FOUND_ON_WHATSAPP, the bff history
    // to RECIPIENT_NOT_FOUND_ON_PROVIDER.
    if (
      message.error === MessageErrorType.ERROR_RECIPIENT_NOT_FOUND_ON_WHATSAPP ||
      message.error === MessageErrorType.RECIPIENT_NOT_FOUND_ON_PROVIDER
    ) {
      return resolveWhatsappWebRecipientNotFoundNotice();
    }
    return undefined;
  }
  if (channel !== eChannelType.WHATSAPP_API) return undefined;
  if (!message?.fromMe || message.state !== MessageState.ERROR)
    return undefined;

  // INT-1500 U5: Meta is reviewing it, nothing failed. Checked before the
  // catalog, so Meta's synthetic code on the row (900001) never reads as an
  // unmapped failure. A later refusal changes the error and the notice.
  if (message.error === MessageErrorType.WAPI_HELD_FOR_QUALITY_ERROR) {
    return resolveHeldForQualityNotice();
  }

  const notice = catalogNoticeFor(message, outgoingCopyVariantFor(message));
  return notice?.direction === 'outgoing' ? notice : undefined;
}

// Meta's words when the link it was given answers "forbidden" or "not found".
// Our storage answers 403 for a file that no longer exists.
const FILE_GONE_DETAILS =
  /Downloading media from weblink failed with http code (403|404)\b/i;

// Meta's words when it refuses a WebP sent as an image.
const WEBP_DETAILS = /WebP image uploads are not currently supported/i;

/**
 * INT-1683: 131053 reads as a WebP Meta does not take when its details say so,
 * whoever served the file: saving it as JPG or PNG is the way out.
 *
 * BUG-912: 131053 reads as a file gone from the workspace's storage when Meta
 * could not fetch it for that reason and the file was ours to serve. Quick
 * replies and Leadbot flows keep sending the link of a file deleted in
 * Ajustes → Almacenamiento, and resending never works: the file has to be
 * attached again. Any other 131053 (a transient fetch failure, another
 * format Meta rejects, a link that is not ours) keeps the entry's wording and
 * its support action.
 */
function outgoingCopyVariantFor(
  message: tMetaErrorNoticeSource,
): tMetaErrorNoticeCopyVariant | undefined {
  const { metaCode, details } = metaFactsOf(message.errors);
  if (metaCode !== 131053 || !details) return undefined;
  if (WEBP_DETAILS.test(details)) return 'webp';
  if (!FILE_GONE_DETAILS.test(details)) return undefined;
  const sentOurFile = message.attachments?.some(
    (attachment) => storageFileIdFromUrl(attachment?.url) !== undefined,
  );
  return sentOurFile ? 'fileGone' : undefined;
}

/**
 * INT-1506: the notice for a message the lead sent that did not reach us
 * whole: a file Meta could not download (131052), or a message Meta did not
 * share (131060, 131051). Undefined for everything else, which keeps its
 * previous display: an inbound error is not a delivery failure, so the
 * outgoing entries never apply to it.
 *
 * INT-1500 U4: it also explains the business's own message echoed from the
 * WhatsApp Business app (coexistence) when Meta did not share that either
 * (131060, 131051: the entries marked `echoes`). 131051 reads as an edit for
 * 3063 and as the business's phone for an echo.
 */
export function resolveIncomingMetaErrorNotice(
  message: tMetaErrorNoticeSource | null | undefined,
  channel: eChannelType | string | undefined,
): tMetaErrorNotice | undefined {
  if (channel !== eChannelType.WHATSAPP_API) return undefined;
  if (!message || message.state !== MessageState.ERROR) return undefined;

  const notice = catalogNoticeFor(message, copyVariantFor(message));
  if (notice?.direction !== 'incoming') return undefined;
  return !message.fromMe || notice.echoes ? notice : undefined;
}

function copyVariantFor(
  message: tMetaErrorNoticeSource,
): tMetaErrorNoticeCopyVariant | undefined {
  if (message.fromMe) return 'echo';
  if (message.error === MessageErrorType.WAPI_EDITED_MESSAGE_ERROR) return 'edit';
  return undefined;
}

/**
 * The Meta codes of error names the notice explains that
 * messageErrorTypeToMetaCode does not carry. That map also drives the legacy
 * inline card and toasts, and every value in it needs a legacy ErrorDirectory
 * entry; these names have none on purpose.
 *
 * INT-1500 U4: Meta 131051 as chats-service splits it per subtype (BUG-704):
 * 3050 no subtype, 3063 an edit, 3064 any other. BUG-587 keeps them off the
 * inline card.
 */
const NOTICE_ONLY_META_CODES: Readonly<Partial<Record<string, string>>> = {
  [MessageErrorType.WAPI_UNSUPPORTED_MESSAGE_TYPE_ERROR]: '131051',
  [MessageErrorType.WAPI_EDITED_MESSAGE_ERROR]: '131051',
  [MessageErrorType.WAPI_GENERIC_UNSUPPORTED_MESSAGE_ERROR]: '131051',
};

/**
 * INT-1500 C3: Meta's own code and details on the error row the message's
 * error came from (the first one, as `parseErrors` reads it). A code that is
 * not a whole number is not Meta's and is ignored.
 */
function metaFactsOf(errors: tChatEventError[] | undefined): {
  metaCode?: number;
  details?: string;
} {
  const facts = errors?.[0]?.error;
  // Typed as a number, but it crosses two services: accept a numeric string,
  // never anything else.
  const raw: unknown = facts?.metaCode;
  const metaCode =
    typeof raw === 'number' || (typeof raw === 'string' && /^\d+$/.test(raw))
      ? Number(raw)
      : Number.NaN;
  const details =
    typeof facts?.details === 'string' && facts.details.trim() !== ''
      ? facts.details
      : undefined;
  return {
    metaCode: Number.isInteger(metaCode) ? metaCode : undefined,
    details,
  };
}

/**
 * The catalog notice for a chat error, or undefined when the error names no
 * Meta code and is no refusal of ours. Meta's own code on the error row wins
 * (C3): Meta itself refused. Without it, a refusal of ours gets its own notice
 * (INT-1500 U7), with the hold facts its row carries (C4); anything else maps
 * the internal code. Codes we mint ourselves (`wba-*`) never reach the Meta
 * catalog.
 */
function catalogNoticeFor(
  source: tMetaErrorNoticeSource,
  copyVariant?: tMetaErrorNoticeCopyVariant,
): tMetaErrorNotice | undefined {
  const { metaCode, details } = metaFactsOf(source.errors);
  const refusal =
    metaCode === undefined
      ? resolveLeadsalesRefusalNotice(source.error, source.errors?.[0]?.error ?? {})
      : undefined;
  if (refusal) return refusal;

  const code =
    metaCode ??
    messageErrorTypeToMetaCode[source.error ?? ''] ??
    NOTICE_ONLY_META_CODES[source.error ?? ''];
  if (code === undefined) return undefined;
  if (typeof code === 'string' && code.startsWith('wba-')) return undefined;

  const notice = resolveMetaErrorNotice(
    code === PERMISSION_RANGE_SENTINEL ? '200-299' : code,
    { copyVariant },
  );
  return details ? { ...notice, technicalDetail: details } : notice;
}

const RESOLVING_STATES: ReadonlySet<MessageState> = new Set([
  MessageState.SENT,
  MessageState.DELIVERED,
  MessageState.READ,
]);

/**
 * INT-1504: the outgoing message that resolved a refused one, if any: the
 * first message the business sent after it that WhatsApp accepted.
 *
 * Derived from the history on purpose, so the "Resuelto" state survives a
 * reload without the backend storing it. A sent template is not marked as a
 * template on its event, so any later accepted outgoing message counts, and
 * the copy says "otro mensaje", not "otra plantilla". A retry of the same
 * message needs none of this: it flips that event's own state.
 */
export function findResolvingMessage(
  failed: Pick<tChatEvent, 'id' | 'createdAt'>,
  events: tChatEvent[],
): tChatEvent | undefined {
  const failedAt = Date.parse(failed.createdAt);
  if (Number.isNaN(failedAt)) return undefined;

  let resolving: tChatEvent | undefined;
  let resolvingAt = Infinity;
  for (const candidate of events) {
    if (candidate.id === failed.id) continue;
    if (
      candidate.type !== EventType.MESSAGE &&
      candidate.type !== EventType.TEMPLATE
    )
      continue;
    if (!candidate.message?.fromMe) continue;
    if (!RESOLVING_STATES.has(candidate.message.state)) continue;

    const sentAt = Date.parse(candidate.createdAt);
    if (sentAt > failedAt && sentAt < resolvingAt) {
      resolving = candidate;
      resolvingAt = sentAt;
    }
  }
  return resolving;
}

export function resolvedTimeFormat(date: string) {
  return moment(date).format('h:mm A');
}
